import type { UserRole } from './admin.ts';

export type ComputeResourceTier = 'free' | 'go' | 'basic' | 'pro';
export interface ComputeResourceLimits { memoryLimitBytes: number; cpuLimit: number }
export interface ComputeResourceEntitlement extends ComputeResourceLimits {
  tier: ComputeResourceTier;
  source: 'default' | 'subscription' | 'administrator';
}

/** Account-wide ceilings for cloud tools; these do not reserve host capacity. */
export const COMPUTE_RESOURCE_LIMITS = Object.freeze({
  free: Object.freeze({ memoryLimitBytes: 1024 ** 3, cpuLimit: 1 }),
  go: Object.freeze({ memoryLimitBytes: 1024 ** 3, cpuLimit: 1 }),
  basic: Object.freeze({ memoryLimitBytes: 2 * 1024 ** 3, cpuLimit: 1 }),
  pro: Object.freeze({ memoryLimitBytes: 2 * 1024 ** 3, cpuLimit: 2 }),
});

/** Call with authoritative account roles and completed subscription periods only. */
export function resolveComputeResourceLimits(input: {
  role?: UserRole;
  periods: ReadonlyArray<{ planId: string; startsAt?: string; expiresAt: string }>;
}, now = Date.now()): ComputeResourceEntitlement {
  if (!Number.isFinite(now)) throw new Error('Invalid resource evaluation time');
  if (input.role === 'admin' || input.role === 'super_admin') {
    return { ...COMPUTE_RESOURCE_LIMITS.pro, tier: 'pro', source: 'administrator' };
  }
  const tiers = ['free', 'go', 'basic', 'pro'] as const;
  const timestamp = (value: string): number => {
    const time = Date.parse(value);
    return Number.isFinite(time) && new Date(time).toISOString() === value ? time : NaN;
  };
  let selected = 0;
  for (const period of input.periods) {
    const rank = tiers.findIndex(tier => tier === period.planId);
    const expiry = timestamp(period.expiresAt);
    const start = period.startsAt === undefined ? -Infinity : timestamp(period.startsAt);
    if (rank > selected && Number.isFinite(expiry) && expiry > now && start <= now && start < expiry) selected = rank;
  }
  const tier = tiers[selected];
  return { ...COMPUTE_RESOURCE_LIMITS[tier], tier, source: tier === 'free' ? 'default' : 'subscription' };
}
